// @flow

import React, { useState } from "react";
import { compose } from "redux";
import { localize } from "redux-i18n";
import { formatDateTime } from "@transformers/date";
import { BoxField, Button, GSGrid, SwitchButton, Checkbox, Text, View } from "@components";
import type {
  DataSecurityGeneralSettings,
  IpAddress,
} from "@modules/service/reducer/system-access/types";

type Props = $Exact<{
  list: Array<IpAddress>,
  settings: ?DataSecurityGeneralSettings,
  onUpdateSettings: (settings: DataSecurityGeneralSettings) => void,
  onAdd: () => void,
  ...PropsFromLocalize,
}>;

type State = $Exact<{
  checkedIpAddresses: Array<IpAddress>,
  isSectionOpen: boolean,
}>;

export function SystemAccessIpAddresses({ list, settings, t, onUpdateSettings, onAdd }: Props) {
  const [checkedIpAddresses, setCheckedIpAddresses] = useState([]);
  const [isSectionOpen, setIsSectionOpen] = useState(true);
  const enabled = (settings || {}).ipRestrictionEnabled;
  const columns = [
    {
      name: t("IP_ADDRESS"),
      key: "value",
      align: "left",
    },
    {
      name: t("DATE_ADDED"),
      key: "startTime",
      renderCell: ({ row }: { row: IpAddress }) => GSGrid.render.dateTime(row.activeFrom),
    },
    {
      name: t("DATE_DISABLED"),
      key: "endTime",
      renderCell: ({ row }: { row: IpAddress }) => GSGrid.render.dateTime(row.activeTo),
    },
    {
      name: t("STATUS"),
      key: "isEnabled",
      renderCell: ({ row }: { row: IpAddress }) =>
        GSGrid.render.statusPill(t(row.isEnabled ? "ACTIVE" : "INACTIVE")),
    },
  ];

  function changeHandler(selectedRows: [string]) {
    setCheckedIpAddresses(selectedRows);
  }

  function isChecked(value: string) {
    return checkedIpAddresses.some(ipAddress => {
      return ipAddress.value == value;
    });
  }

  function toggleSectionOption() {
    setIsSectionOpen(_ => !isSectionOpen);
  }

  function updateEnabled(ipRestrictionEnabled: boolean) {
    onUpdateSettings({ ...settings, ipRestrictionEnabled });
    if (!ipRestrictionEnabled) {
      setCheckedIpAddresses([]);
    }
  }

  function restoreIpAddresses() {
    onUpdateSettings({
      ...settings,
      ipRestrictionWhitelist: list.map((record: IpAddress) => {
        if (checkedIpAddresses.includes(record.value)) {
          return {
            ...record,
            isEnabled: true,
            activeTo: null,
            activeFrom: new Date().toISOString(),
          };
        } else return record;
      }),
    });
    deselectAllHandler();
  }

  function disableIpAddresses() {
    onUpdateSettings({
      ...settings,
      ipRestrictionWhitelist: list.map((record: IpAddress) => {
        if (checkedIpAddresses.includes(record.value)) {
          return {
            ...record,
            isEnabled: false,
            activeTo: new Date().toISOString(),
          };
        } else return record;
      }),
    });
    deselectAllHandler();
  }

  function deselectAllHandler() {
    setCheckedIpAddresses([]);
  }

  function selectAll() {
    setCheckedIpAddresses(_ => list.map(l => l));
  }

  return (
    <View gap={6}>
      <View direction="row" justifyContent="space-between">
        <Text type="h6">{t("SYSTEM_ACCESS")}</Text>
        {enabled && (
          <Button materialIcon="add_box" className="add-ip-address" onClick={onAdd} size="small">
            {t("ADD")}
          </Button>
        )}
      </View>
      {settings && (
        <BoxField title={t("IP_RESTRICTION")} description={t("IP_RESTRICTION_DESCRIPTION")}>
          <SwitchButton active={enabled} onChange={updateEnabled} />
        </BoxField>
      )}
      {!!list?.length && enabled && (
        <GSGrid
          onSelectedRowsChange={changeHandler}
          selectedRows={checkedIpAddresses}
          rowKeyGetter={row => row.value}
          rows={list}
          columns={columns}
        />
      )}
      {checkedIpAddresses.length !== 0 && enabled && (
        <View direction="row" gap={2} alignItems="center">
          <Text color="orange">
            {t("RECORDS_SELECTED", {
              numberOfRecords: checkedIpAddresses.length,
            })}
          </Text>
          <Button
            size="small"
            color="positive"
            className="mr-3 restore-ip-addresses"
            onClick={restoreIpAddresses}
          >
            {t("RESTORE")}
          </Button>
          <Button size="small" color="negative" onClick={disableIpAddresses}>
            {t("DISABLE")}
          </Button>
          <Button size="small" color="secondary" onClick={deselectAllHandler}>
            {t("DESELECT_ALL")}
          </Button>
        </View>
      )}
    </View>
  );
}

export default compose(localize())(SystemAccessIpAddresses);
